import {QueryClient, QueryClientProvider, QueryKey, useQueryClient} from "@tanstack/react-query";
import {createContext, PropsWithChildren, useCallback, useContext, useEffect, useState} from "react";
import {fullResetEventType} from "../reset.tsx";

// this should probably be coordinated with zrpc's timeout for idle
// outlet subscriptions to some extent.
export const DEFAULT_STALE_MILLIS = 40 * 60 * 1000;

export interface Clearer {
    clear: () => void;
}

const BedrockClearerContext = createContext<Clearer>({
    clear: () => {/**/
    }
});

export type BedrockProviderProps = PropsWithChildren<{ bedrockKeys: QueryKey[] }>

// do not try to hardcode bedrock keys, lest you cause a cascade of
// circular imports. they are the keys that persist across hard resets.
function BedrockProvider({bedrockKeys, children}: BedrockProviderProps) {
    const tqc = useQueryClient();
    const clear = useCallback(() => {
        const vault = bedrockKeys.map(qk => {
            const v = tqc.getQueryData(qk);
            return {qk, v};
        });
        tqc.clear();
        vault.forEach(({qk, v}) => {
            tqc.setQueryData(qk, v);
        });
    }, [bedrockKeys, tqc]);

    // make sure we clear on a full reset
    useEffect(() => {
        document.addEventListener(fullResetEventType, clear);
        return () => {
            document.removeEventListener(fullResetEventType, clear);
        }
    }, [clear]);

    // const clearer = useMemo(() => {
    //     return {clear};
    // }, [clear]);

    const cache = tqc.getQueryCache();
    useEffect(() => {
        return cache.subscribe((evt) => {
            if (evt.type === "updated" && evt.query.state.status === "success") {
                console.log(`cache update ${JSON.stringify(evt.query.queryKey)} ${JSON.stringify(evt.query.state.data)}`);
            }
        });
    }, [cache]);

    return <>{children}</>;
}

export function useQueryCacheClearer() {
    return useContext(BedrockClearerContext);
}

export function TanstackQueryProvider({bedrockKeys, children}: BedrockProviderProps) {
    const [tqc] = useState(() => new QueryClient({
        defaultOptions: {
            queries: {
                staleTime: DEFAULT_STALE_MILLIS,
                throwOnError: true,
            }
        }
    }));

    return <QueryClientProvider client={tqc}>
        <BedrockProvider bedrockKeys={bedrockKeys}>
            {children}
        </BedrockProvider>
    </QueryClientProvider>;
}

